import '../Main.scss';

import { connect } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import * as React from 'react';

import {
    Col,
    Container,
    Row
} from 'reactstrap';

import { Theme, ThemeProvider } from '@mui/material/styles';

import { ApplicationState } from '../../Store';
import {
    actionCreators as AppSettingActions,
    GetDefaultSubmissionOptions,
    GetStrongboxUrl,
} from '../../Store/AppSettings';
import {
    BorrowerActionCreators,
    GetAcceptedBusinessInfo,
    GetBorrowerAnonymousState,
    GetBorrowerId,
    GetBorrowerSubmissionId,
} from '../../Store/Borrower';
import {
    ConnectorType,
    GetBorrowerUniversal,
    actionCreators as ImportFinancialsActions,
    LinkMode,
} from '../../Store/ImportFinancials';
import {
    GetRequestedOptions,
    actionCreators as NewDataCollectionParametersActions,
} from '../../Store/NewDataCollectionParameters';
import {
    BrandConfig,
    GetBrandConfig,
    GetTenantName,
    IsBorrowerDisabled,
    TenantActionCreators,
} from '../../Store/Tenant';
import { GetPortalBusyState, actionCreators as UIStateActions } from '../../Store/UIState';

import { PortalFooter, portalFooterId } from '../PortalFooter';
import { PortalHeader } from '../PortalHeader';

import {
    BorrowerSectionIds,
    pathConstants,
} from '../../Utils/Constants';
import { LogMessage, SeverityLevel } from '../../Utils/Logging';
import { GetWorkspaceIdFromUrl } from '../../Utils/PathUtils';
import { StringFormat } from '../../Utils/StringUtils';
import { CreateStdTheme } from '../../Utils/Style';

import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';

import { StrongboxTOU } from '../StrongboxTOU';

import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';
import { GetsertWorkspaceEntity } from '../../Services/WorkspaceService';
import { BorrowerParametersContainer } from '../BorrowerParameters/BorrowerParametersContainer';
import { DataCollectionImportFlow } from '../FinancialsImport/DataCollectionImportFlow';
import { AccountingPkgPresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';

import { PortalTextCommon, PortalTextCommonImport } from '../../Models/Api/strongbox.financialportal';
import {
    GetPortalText,
    RetrieveAccountingSystemsText,
    RetrieveBorrowerAnonymousText,
    RetrieveLoginText,
    RetrieveText,
} from '../../Store/PortalAssets';

export type BorrowerPortalChildProps = {
    welcome: string;
    borrowerId: string;
    submissionId: string;
    strongboxUrl: string;
    working: boolean;
    tenantName: string;
    setIsWorking: (working: boolean) => void;
    setOverrideWorking: (override: boolean) => void;

    scrollTop?: () => void;

    importAccountingPkgAnonymous: (accountingPkg: AccountingPkgPresentation, workspaceInfoValid: boolean) => void;
    importAccountingPkg: (accountingPkg: AccountingPkgPresentation, allowAnonymous?: boolean) => void;
}

export type BorrowerPortalProps = {
    children: (props: BorrowerPortalChildProps) => JSX.Element;
    showTOU?: boolean;
    scrollableContent?: boolean;
}

type InjectedReduxState = {
    borrowerId: string;
    submissionId: string;
    strongboxUrl: string;
    borrowerTheme: Theme;
    tenantDisabled: boolean;
    commonText?: PortalTextCommon;
    portalIsBusy: boolean;
    tenantName: string;
    brandConfig: BrandConfig;
    useUniversalConnector: boolean;
    shareableLinkOptions: PortalCreateSubmissionRequestOptions | undefined;
    defaultAnonymousSubmissionOptions: PortalCreateSubmissionRequestOptions;
    acceptedBusinessInfo: { name?: string; email?: string };
    anonymousLink: boolean;
}

type InjectedActionCreators = typeof UIStateActions & typeof AppSettingActions & typeof TenantActionCreators
    & typeof ImportFinancialsActions & typeof NewDataCollectionParametersActions & typeof BorrowerActionCreators;

type Props = BorrowerPortalProps & InjectedReduxState & InjectedActionCreators;

type ContentDimensions = {
    headerHeight: number;
    footerHeight: number;
    containerHeight: number;
    mainContentHeight: number;
}

const idHeaderRow = 'borrower-portal-header-row';
const idWindowContainer = 'main-window-containing-background';

const BorrowerPortalV2Component: React.FC<Props> = (props): React.ReactElement => {
    const {
        AllowHomeNavigate,
        borrowerId,
        showTOU,
        tenantDisabled,
        SetPortalWorking,
        SetPortalIdle,
        portalIsBusy,
        tenantName,
        commonText,
        GetDataCollectionLimit,
        scrollableContent,
        GetBasicTenantSettings,
        LoadAccountingPackageDefinitions,
        useUniversalConnector,
        shareableLinkOptions,
        defaultAnonymousSubmissionOptions,
        SetCurrentSubmissionRequest,
        SetSelectedAccountingPackage,
        SetBorrowerParametersActive,
        SetBorrowerId,
        acceptedBusinessInfo,
        anonymousLink,
    } = props;

    const navigate = useNavigate();
    const location = useLocation();

    const isWorkspaceKnown = (): boolean => {
        return (GetWorkspaceIdFromUrl(location.search).trim() !== '') || (borrowerId.trim() !== '');
    }

    // Borrower package awaiting the parameters/security gate. While set (with the store's parameters-active
    // flag on), BorrowerParametersContainer shows; continuing from it starts the connect flow.
    const [pendingConnectPkg, setPendingConnectPkg] = React.useState<AccountingPkgPresentation | undefined>(undefined);

    const [working, setWorking] = React.useState<boolean>(false);
    const [overrideWorking, setOverrideWorking] = React.useState<boolean>(false);

    const [containerKey, setContainerKey] = React.useState<number>(1);

    const [initializing, setInitializing] = React.useState<boolean>(false);

    const [contentDimensions, setContentDimensions] = React.useState<ContentDimensions | undefined>(undefined);
    const [containerHeight, setContainerHeight] = React.useState<number>(0);

    // Initialization mirrors the original BorrowerPortal so the refactored flow starts from the
    // same state (text, data collection limits, tenant settings and accounting packages loaded).
    const initializeBorrowerFlow = async (): Promise<void> => {
        const workingDescriptor = 'InitializingBorrowerFlow';

        SetPortalWorking(workingDescriptor);
        setInitializing(true);
        try {
            // Back end caches text so repeated calls happen quick.
            await RetrieveBorrowerAnonymousText();
            await RetrieveAccountingSystemsText();
            await RetrieveLoginText();
            // Borrower text is only readable once a workspace exists (from the secure link, or restored from
            // the session). Without one this is the anonymous flow, which loads it after creating the workspace.
            if (isWorkspaceKnown()) {
                await RetrieveText(BorrowerSectionIds);
            }
            await GetDataCollectionLimit(true);
            await GetBasicTenantSettings();
            await LoadAccountingPackageDefinitions();

            setContainerKey(containerKey + 1);
        } finally {
            SetPortalIdle(workingDescriptor);
            setInitializing(false);
        }
    }

    React.useEffect(() => {
        initializeBorrowerFlow();
        AllowHomeNavigate(false);

        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function HandleWindowResize(e: UIEvent) {
        setContainerHeight(window.innerHeight);
    }

    React.useEffect(() => {
        window.addEventListener('resize', HandleWindowResize, true);
        setContainerHeight(window.innerHeight);

        return () => { window.removeEventListener('resize', HandleWindowResize, true); }
    }, []);

    React.useLayoutEffect(() => {
        let footerRow = undefined;

        const headerRow = document.getElementById(idHeaderRow);
        if (showTOU) {
            footerRow = document.getElementById(portalFooterId);
        }
        const containerWindow = document.getElementById(idWindowContainer);

        if (!(headerRow && (!showTOU || footerRow) && containerWindow)) {
            return;
        }

        const rcHeader = headerRow.getBoundingClientRect();
        const rcFooter = !!footerRow ? footerRow.getBoundingClientRect() : undefined;
        const rcContainer = containerWindow.getBoundingClientRect();

        setContentDimensions({
            headerHeight: rcHeader.height,
            footerHeight: !!rcFooter ? rcFooter.height : 0,
            containerHeight: rcContainer.height,
            mainContentHeight: !!rcFooter ? (rcFooter.top - rcHeader.bottom) : (rcContainer.bottom - rcHeader.bottom),
        });

        // Don't need showTOU in the dependency list, it doesn't change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerHeight, initializing]);

    const scrollTop = React.useCallback((): void => {
        const scrollableContainer = document.getElementById('scrollable-content-container');
        if (!!scrollableContainer) {
            scrollableContainer.scrollTo(0, 0);
        }
    }, []);

    // Starts the shared NewDataCollectionParameters connect flow for a borrower package selection. Reuses the
    // link's options (hasShareableLink: false so the borrower always connects directly). The DataCollectionImportFlow
    // rendered at the bottom of this component drives the resulting confirm/connect/working/error UI, so the flow
    // works on every borrower child page — the initial submission (PortalSubmissionV2) AND "import additional
    // financials" on the Linked page.
    // workspaceIdOverride lets the just-created anonymous workspace id be used immediately, before the
    // SetBorrowerId dispatch has propagated to the borrowerId prop.
    const startDataCollectionFlow = React.useCallback((accountingPkg: AccountingPkgPresentation, workspaceIdOverride?: string): void => {
        const options: PortalCreateSubmissionRequestOptions = {
            ...(shareableLinkOptions ?? defaultAnonymousSubmissionOptions),
            hasShareableLink: false,
        };

        SetCurrentSubmissionRequest({
            workspaceId: workspaceIdOverride ?? borrowerId,
            workspaceName: '',
            linkMode: LinkMode.direct,
            connectorType: ConnectorType.fullIntegration,
            options,
            // Borrower secure-URL flow reuses an existing connection (reuse/disconnect) when one is found.
            sourceFlow: 'borrower',
            // The anonymous borrower can't create a submission request or list connections (401); its flow
            // skips both and creates the submission directly. See IDataCollectionSubmissionRequest.isAnonymous.
            isAnonymous: anonymousLink,
        });
        SetSelectedAccountingPackage(accountingPkg.featureName as string);
    }, [shareableLinkOptions, defaultAnonymousSubmissionOptions, borrowerId, anonymousLink, SetCurrentSubmissionRequest, SetSelectedAccountingPackage]);

    // Anonymous flow (user went to /submission with no id): the anonymous borrower's workspace AUTHORIZATION
    // is bootstrapped by GetsertWorkspaceEntity (POST /api/Entities). It must run before ANY workspace-scoped
    // call (SubmissionRequests, connections, the connect) or those 401. It is getsert, so re-running it on a
    // later import returns the SAME workspace id and re-establishes the session — which is why BOTH the initial
    // import and "import additional financials" route through here (the second import would otherwise 401). We
    // then go STRAIGHT to the connect modal, skipping the parameters/security gate. The id is passed to
    // startDataCollectionFlow directly since the SetBorrowerId dispatch has not yet propagated to the borrowerId prop.
    const importAccountingPkgAnonymous = React.useCallback(async (accountingPkg: AccountingPkgPresentation, workspaceInfoValid: boolean): Promise<void> => {
        scrollTop();
        // All package buttons are disabled until the business-info fields are valid, so this is a sanity check.
        if (!workspaceInfoValid) {
            return;
        }

        const workingSource = 'BorrowerPortalV2.CreateAnonymousWorkspace';
        SetPortalWorking(workingSource);
        let id: string | undefined;
        try {
            id = await GetsertWorkspaceEntity(acceptedBusinessInfo.name || '', acceptedBusinessInfo.email || '');
            if (!!id) {
                // Creating the workspace grants the permission to read borrower text; sections already in the
                // store are skipped, so "import additional financials" re-entering here costs nothing.
                await RetrieveText(BorrowerSectionIds);
            }
        } finally {
            SetPortalIdle(workingSource);
        }

        if (!id) {
            LogMessage('Failed to create the anonymous borrower workspace entity', SeverityLevel.Error, { name: acceptedBusinessInfo.name });
            return;
        }

        SetBorrowerId(id);
        startDataCollectionFlow(accountingPkg, id);
    }, [scrollTop, SetPortalWorking, SetPortalIdle, acceptedBusinessInfo, SetBorrowerId, startDataCollectionFlow]);

    // Borrower package selection (from PortalSubmissionV2's picker or the Linked page's "import additional
    // financials"): gate on the parameters/security modal, then start the connect flow on continue. The
    // anonymous flow skips the gate entirely and routes through importAccountingPkgAnonymous so every import
    // (initial AND "import additional financials") re-bootstraps the anonymous workspace session; calling
    // startDataCollectionFlow directly here would 401 on the second import (no session re-established).
    const importAccountingPkg = React.useCallback((accountingPkg: AccountingPkgPresentation): void => {
        scrollTop();
        LogMessage(
            `User selected an accounting package in the borrower flow ${accountingPkg.friendlyName}`,
            SeverityLevel.Information,
            { ...accountingPkg }
        );
        if (anonymousLink) {
            importAccountingPkgAnonymous(accountingPkg, true);
            return;
        }
        setPendingConnectPkg(accountingPkg);
        SetBorrowerParametersActive(true);
    }, [scrollTop, anonymousLink, importAccountingPkgAnonymous, SetBorrowerParametersActive]);

    const childProps = React.useMemo(() => {
        const result: BorrowerPortalChildProps = {
            ...props,
            welcome: !!commonText ? StringFormat(commonText.welcomeMessage, tenantName) : '',
            working,
            tenantName,
            setIsWorking: setWorking,
            setOverrideWorking: setOverrideWorking,
            scrollTop,
            importAccountingPkgAnonymous,
            importAccountingPkg,
        };

        return result;

    }, [props, commonText, tenantName, working, scrollTop, borrowerId, importAccountingPkg, importAccountingPkgAnonymous]);

    if (tenantDisabled) {
        return <Navigate to={pathConstants.tenantBorrowerDisabled} />
    }

    return (
        <ThemeProvider theme={props.borrowerTheme}>
            {(!initializing && useUniversalConnector) ? (
                // Universal-collector secure link: no portal header or footer — just a full-height centered
                // spinner while the auto-connect resolves and redirects into the file-upload experience. The
                // children still mount (PortalSubmissionV2 runs the auto-connect); the parent flow below drives it.
                <div key={containerKey.toString()} className={'borrower-window-container'} id={idWindowContainer}>
                    <LoadingIndicator
                        centerIndicator={true}
                        active={true}
                        size={50}
                        thickness={6}
                    />
                    {!!props.children && props.children(childProps)}
                </div>
            ) : (
                <div key={containerKey.toString()}>
                    <Container
                        fluid
                        className={'borrower-window-container'}
                        id={idWindowContainer}
                    >
                        {!initializing && (
                            <Row
                                className={'portal-header-row'}
                                id={idHeaderRow}
                            >
                                <Col className={'gx-0'}>
                                    <PortalHeader
                                        hideMenu={true}
                                        hideDataCollectionsRemaining={true}
                                        flow={'borrower'}
                                        centeredLogoOnly={true}
                                    />
                                </Col>
                            </Row>
                        )}
                        {!!contentDimensions && (
                            <Row
                                className={`borrower-content-container control-region-borrower`}
                                style={{
                                    height: contentDimensions.mainContentHeight,
                                }}
                            >
                                {initializing && (
                                    <Col xs={{ size: 12 }} md={{ size: 8, offset: 2 }}>
                                        <LoadingIndicator
                                            centerIndicator={true}
                                            active={(working && !overrideWorking) || portalIsBusy}
                                            size={50}
                                            thickness={6}
                                        />
                                    </Col>
                                )}
                                {/* Reaching here with !initializing implies !useUniversalConnector (the UC case is
                                    the standalone spinner above), so this is always the normal picker card. */}
                                {!initializing && (
                                    <Col xs={{ size: 12 }} md={{ size: 8, offset: 2 }} className={'borrower-col'}>
                                        <div className={`borrower-content ${scrollableContent === true ? 'borrower-content-fixed-height' : 'borrower-content-variable-height'}`} id={'scrollable-content-container'}>
                                            <LoadingIndicator
                                                centerIndicator={true}
                                                active={(working && !overrideWorking) || portalIsBusy}
                                                size={50}
                                                thickness={6}
                                            />
                                            {!!props.children && props.children(childProps)}
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        )}
                        {showTOU && !initializing && (
                            <Row>
                                <Col>
                                    <PortalFooter
                                        contentOverride={(<StrongboxTOU />)}
                                    />
                                </Col>
                            </Row>
                        )}
                    </Container>
                </div>
            )}
            {/* The shared connect flow is mounted here at the parent so its parameters-gate and
                confirm/connect/working/error modals are available on EVERY borrower child page — the initial
                submission (PortalSubmissionV2) and "import additional financials" on the Linked page. Both
                render nothing unless a flow/gate is active. */}
            <DataCollectionImportFlow onImportStarted={() => navigate(pathConstants.progress)} />
            <BorrowerParametersContainer
                accountingPkg={pendingConnectPkg}
                dismissModal={(cancelled: boolean) => {
                    SetBorrowerParametersActive(false);
                    if (!cancelled && !!pendingConnectPkg) {
                        startDataCollectionFlow(pendingConnectPkg);
                    }
                    setPendingConnectPkg(undefined);
                }}
            />
        </ThemeProvider>
    );
}

export const BorrowerPortalV2 = connect<InjectedReduxState, InjectedActionCreators, BorrowerPortalProps, ApplicationState>(
    (appState: ApplicationState) => {
        const brandConfig = GetBrandConfig(appState);

        const commonText = GetPortalText<PortalTextCommon>(appState, 'commonText');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        const result: InjectedReduxState = {
            borrowerId: GetBorrowerId(appState),
            submissionId: GetBorrowerSubmissionId(appState),
            strongboxUrl: GetStrongboxUrl(appState) || '',
            borrowerTheme: CreateStdTheme(brandConfig),
            tenantDisabled: IsBorrowerDisabled(appState),
            commonText,
            portalIsBusy: GetPortalBusyState(appState),
            tenantName: GetTenantName(appState),
            brandConfig,
            useUniversalConnector: GetBorrowerUniversal(appState),
            shareableLinkOptions: GetRequestedOptions(appState),
            defaultAnonymousSubmissionOptions: GetDefaultSubmissionOptions(appState, 'shareableanonymous', true, commonImportText),
            acceptedBusinessInfo: GetAcceptedBusinessInfo(appState),
            anonymousLink: GetBorrowerAnonymousState(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...UIStateActions,
            ...AppSettingActions,
            ...TenantActionCreators,
            ...ImportFinancialsActions,
            ...NewDataCollectionParametersActions,
            ...BorrowerActionCreators,
        },
        dispatch
    )
)(BorrowerPortalV2Component);
